import {inject} from '@angular/core';
import {Routes} from '@angular/router';
import {GenRoleDefinition} from 'generated/serverModels/GenRoleDefinition';
import {AppEnvironment} from 'src/app/shared/enums/environment.enum';
import {AuthenticatedUserGuard} from 'src/app/shared/guards/authenticated-user.guard';
import {LocationNameGuard} from 'src/app/shared/guards/location-name.guard';
import {OverrideDataGuard} from 'src/app/shared/guards/override-data.guard';
import {ReportGuard} from 'src/app/shared/guards/report.guard';
import {RoleGuard} from 'src/app/shared/guards/role.guard';
import {StopTimeGuard} from 'src/app/shared/guards/stop-time.guard';
import {SystemEventService} from 'src/app/routes/system-event/services/system-event.service';
import {MapLayerCatalogService} from 'src/app/shared/services/map-layer-catalog.service';
import {TermsOfUseComponent} from 'src/app/routes/terms-of-use/terms-of-use.component';
import {UserPreferenceComponent} from './routes/user/components/user-preference/user-preference.component';

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: '/app/landing',
    },
    {
        path: '',
        resolve: {
            locations: LocationNameGuard,
        },
        children: [
            {
                path: 'aviary',
                loadComponent: () => import('./routes/aviary/aviary.component').then((c) => c.AviaryComponent),
            },
            {
                path: 'login',
                data: {},
                loadChildren: () => import('./routes/login/login.routes').then((c) => c.routes),
            },
            {
                path: 'app',
                canActivate: [AuthenticatedUserGuard],
                loadComponent: () => import('./routes/base-page/base-page.component').then((c) => c.BasePageComponent),
                resolve: {
                    overrideData: OverrideDataGuard,
                    stopTime: StopTimeGuard,
                    systemEvent: () => inject(SystemEventService).getGroupedEvents(),
                    mapLayerCatalog: () => inject(MapLayerCatalogService).loadActiveCatalog({force: true}),
                },
                data: {title: (window as any).eaglei?.env === AppEnvironment.TRAINING ? 'SPARROW-ME' : 'EAGLE-I™'},
                loadChildren: () => [
                    {
                        path: 'exporter',
                        canActivate: [AuthenticatedUserGuard],
                        loadComponent: () =>
                            import('./routes/exporter/exporter.component').then((c) => c.ExporterComponent),
                        data: {
                            exporting: true,
                        },
                    },
                    {
                        path: 'resolution',
                        canActivate: [AuthenticatedUserGuard, RoleGuard],
                        loadComponent: () =>
                            import('./routes/resolution-check/resolution-check.component').then(
                                (c) => c.ResolutionCheckComponent,
                            ),
                        data: {
                            requiredRoles: [GenRoleDefinition.ROLE_ADMIN],
                        },
                    },
                    {
                        path: 'landing',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/landing/landing.routes').then((c) => c.routes),
                        data: {
                            title: 'Dashboard',
                            requireUserPreferences: true,
                        },
                    },
                    {
                        path: 'kitchen-sink',
                        loadComponent: () =>
                            import('./routes/kitchen-sink/kitchen-sink.component').then((c) => c.KitchenSinkComponent),
                        data: {title: 'Kitchen Sink'},
                    },
                    {
                        path: 'events',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/events/recent-events.routes').then((c) => c.routes),
                        data: {title: 'Events'},
                    },
                    {
                        path: 'system-event',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/system-event/system-event.routes').then((c) => c.routes),
                        data: {title: 'System Event Management'},
                    },
                    {
                        path: 'systemnews',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/system-news/system-news.routes').then((c) => c.routes),
                        data: {title: 'System News'},
                    },
                    {
                        path: 'help',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/help/help.routes').then((c) => c.routes),
                        data: {title: 'Help'},
                    },
                    {
                        resolve: {
                            report: ReportGuard,
                        },
                        path: 'reports',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/report/report.routes').then((c) => c.routes),
                        data: {title: 'Reports'},
                    },
                    {
                        path: 'apikey',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/api-key/api-key.routes').then((c) => c.routes),
                        data: {title: 'Api Key'},
                    },
                    {
                        path: 'user',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/user/user.routes').then((c) => c.routes),
                        data: {title: 'User'},
                    },
                    {
                        path: 'alerts',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/alert/alert.routes').then((c) => c.routes),
                        data: {title: 'Alerts'},
                    },
                    {
                        path: 'map',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/map/map.routes').then((c) => c.routes),
                        data: {title: 'Mapper'},
                    },
                    {
                        path: 'layers',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/layer/layer.routes').then((c) => c.routes),
                        data: {title: 'Layer'},
                    },
                    {
                        path: 'communities',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/community/community.routes').then((c) => c.routes),
                        data: {title: 'Community'},
                    },
                    {
                        path: 'management',
                        canActivate: [AuthenticatedUserGuard],
                        loadChildren: () => import('./routes/management/management.routes').then((c) => c.routes),
                        data: {title: 'Management'},
                    },
                    {
                        path: 'preferences',
                        component: UserPreferenceComponent,
                        data: {title: 'User Preferences'},
                    },
                ],
            },
        ],
    },
    {
        path: 'attribution',
        loadChildren: () => import('./routes/attribution/attribution.routes').then((c) => c.routes),
        data: {title: 'Attribution'},
    },
    {
        path: 'termsOfUse',
        component: TermsOfUseComponent,
    },
    {
        path: '**',
        redirectTo: '/app/landing',
    },
];
